import React, { useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import { PageHeader } from '../../../ui';
import StepperAdesao from '../../../components/adesao/StepperAdesao';
import FormularioVeiculo, {
  DadosVeiculo,
  valorBaseDoVeiculo,
} from '../../../components/carro/FormularioVeiculo';

import { RootState, store } from '../../../store/store';
import { addCarroData } from '../../../store/associadoSlice';
import { setCodigoData } from '../../../store/codigoSlice';
import { salvarEtapaRascunho } from '../../../utils/adesaoFluxo';

import { DOCUMENTO } from '../../documento/routes';

/**
 * `/carro` — **etapa 2 da adesão** (reescrita na at-59, frente C/C2).
 *
 * É a menor das três telas da família e a **única que grava rascunho**; por isso o
 * `FormularioVeiculo` nasceu a partir dela, foi testado aqui, e só então serviu às
 * duas substituições (ordem obrigatória do `plan-c` §5 → C2).
 *
 * ## O que a reescrita mudou de COMPORTAMENTO
 *
 *   1. **Fim dos 3 segundos de espera** antes de navegar. O toast sobrevive à
 *      navegação, porque o `ToastContainer` é único e vive no `index.tsx` (at-19).
 *   2. **Sem `FullScreenLoader`**: não há chamada síncrona a esperar — o rascunho é
 *      fire-and-forget. O botão vira "Salvando..." e desabilita, sem cobrir a tela.
 *   3. **Erro aparece junto do campo**. Antes o "Salvar" ficava desabilitado
 *      enquanto faltasse qualquer um dos ~20 obrigatórios, **sem dizer qual**.
 *   4. **A tela abre PREENCHIDA** quando já há veículo no fluxo. É o mesmo defeito
 *      que a spec 03 §7.2 nomeia para a etapa 1 — e aqui ele ficou mais fácil de
 *      alcançar, porque esta tela **ganhou um "Voltar"** (não tinha).
 *   5. **Stepper** dizendo "Etapa 2 de 6".
 *
 * ## O que NÃO mudou, de propósito
 *
 * As chaves gravadas no estado (lidas por nome em `views/documento` para montar o
 * `POST /associado/cadastrar`), o contrato do rascunho, e toda a regra de negócio
 * do veículo — que vive no {@link FormularioVeiculo}.
 */
const CarroPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const voltar = useVoltar();

  const associado = useSelector((state: RootState) => state.associado);

  /**
   * Preenchimento inicial a partir do estado do fluxo.
   *
   * ⚠️ Lido **uma vez**, no primeiro render — é o `valoresIniciais` do formulário,
   * que não re-sincroniza depois (ver o comentário lá). Numa adesão nova o slice
   * já foi limpo por `iniciarNovaAdesao`, então isto resulta em formulário vazio.
   */
  const [valoresIniciais] = useState(
    () => (store.getState().associado.carro ?? null) as Partial<DadosVeiculo> | null,
  );

  const salvar = (dados: DadosVeiculo) => {
    dispatch(addCarroData(dados));

    /*
     * O slice `codigo` é o que `/produto` e `/termo` consultam para pedir cota,
     * rateio e produtos — e o "valor base" ali é o PROTEGIDO no veículo comum e na
     * motocicleta elétrica é o único que existe (at-30/at-46).
     */
    dispatch(
      setCodigoData({
        codigoRegional: associado.regionalId,
        codigoCooperativa: dados.cooperativaId,
        codigoTipoVeiculo: dados.tipoVeiculoId,
        valorFipe: valorBaseDoVeiculo(dados),
      }),
    );

    /*
     * Rascunho (at-38): gravado depois dos dois dispatches acima —
     * `store.getState()` porque o `useSelector` desta renderização ainda não vê
     * nenhum deles. Sem `await`: falha de rede não pode atrasar nem impedir a ida
     * para `/documento`.
     */
    void salvarEtapaRascunho(dispatch, () => store.getState(), 'DOCUMENTO');

    // ⚠️ Sem `setTimeout`. O toast sobrevive à navegação (container único).
    navigate(DOCUMENTO());
  };

  return (
    <ShellSaac
      cabecalho={<PageHeader titulo="Veículo" sobretitulo="Nova Adesão" aoVoltar={voltar} />}
    >
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-3xl md:tw-p-6">
          <div className="tw-bg-white md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            <div className="tw-border-b tw-border-superficie-borda">
              <StepperAdesao etapa={2} fluxo={"ADESAO"} />
            </div>

            <div className="tw-p-4 md:tw-p-6">
              <FormularioVeiculo
                rotuloAcao="Continuar — Documentos"
                valoresIniciais={valoresIniciais}
                regionalId={associado.regionalId}
                enderecoAssociado={{
                  rua: associado.rua || '',
                  numero: associado.numero || '',
                  complemento: associado.complemento || '',
                  bairro: associado.bairro || '',
                  cidade: associado.cidade || '',
                  estado: associado.estado || '',
                  cep: associado.cep || '',
                }}
                aoSalvar={salvar}
              />
            </div>
          </div>
        </div>
      </div>
    </ShellSaac>
  );
};

export default CarroPage;
